import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, filter, map, Observable } from 'rxjs';
import { OneTrustConsentGroup } from '../enums/one-trust-consent-group.enum';
import { ArrayUtils } from '../utils/array.utils';

@Injectable({
  providedIn: 'root',
})
export class OneTrustService {
  private activeGroups$ = new BehaviorSubject<OneTrustConsentGroup[]>([]);

  /**
   * Handler that is called when the OneTrust script
   * is loaded, on bootstrap.
   */
  public scriptLoadedHandler(): void {
    this.listenForConsentChanges();
    this.consentChangeHandler();
  }

  /**
   * Observable that broadcasts changes in user consent
   * (active groups). Only emits distinct values.
   */
  public getActiveGroups(): Observable<OneTrustConsentGroup[]> {
    return this.activeGroups$.pipe(
      filter(groups => groups.length > 0),
      distinctUntilChanged()
    );
  }

  /**
   * Observable that broadcasts whether or not the user
   * has given consent for storing/using the given type of cookies.
   */
  public hasGivenConsentFor(group: OneTrustConsentGroup): Observable<boolean> {
    return this.getActiveGroups().pipe(map(activeGroups => activeGroups.includes(group)));
  }

  /**
   * Listen for changes in user consent.
   */
  private listenForConsentChanges(): void {
    if (!OneTrust) return;
    OneTrust.OnConsentChanged(this.consentChangeHandler.bind(this));
  }

  /**
   * Function that runs when user cookie consent changes.
   *
   * In practise this is when:
   * - the user makes changes to their cookie settings in the OneTrust consent dialog.
   * - The function also runs once at bootstrap, when the OneTrust script is loaded
   */
  private consentChangeHandler(): void {
    if (OnetrustActiveGroups === undefined) return;
    const validValues = ArrayUtils.enumToArray(OneTrustConsentGroup);
    const activeGroups = OnetrustActiveGroups.trim()
      .split(',')
      .filter((value): value is OneTrustConsentGroup => value !== '')
      .filter(value => validValues.includes(value as OneTrustConsentGroup));
    this.activeGroups$.next(activeGroups);
  }
}
